import { useMemo, useState } from "react";
import { Loader2, Play, Coins } from "lucide-react";

const selectCls = "w-full rounded-xl px-3 py-2.5 text-sm bg-transparent";
const selectStyle = { border: "1.5px solid var(--border)", background: "var(--paper)", color: "var(--text)" };

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="text-xs uppercase tracking-widest font-semibold block mb-1.5" style={{ color: "var(--text-muted)" }}>{label}</span>
      {children}
    </label>
  );
}

function TopicPicker({ section, value, onChange }) {
  const cat = section.categories.find((c) => c.code === value.category_code);
  const topic = cat?.topics.find((t) => t.id === value.topic_id);
  return (
    <>
      <Field label="Content category">
        <select className={selectCls} style={selectStyle} value={value.category_code || ""}
          onChange={(e) => onChange({ category_code: e.target.value || null, topic_id: null, subtopic: null })}
          data-testid="ts-category-select">
          <option value="">Choose a category…</option>
          {section.categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </Field>
      {cat && (
        <Field label="Topic">
          <select className={selectCls} style={selectStyle} value={value.topic_id || ""}
            onChange={(e) => onChange({ ...value, topic_id: e.target.value || null, subtopic: null })}
            data-testid="ts-topic-select">
            <option value="">Choose a topic…</option>
            {cat.topics.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
      )}
      {topic && topic.subtopics.length > 0 && (
        <Field label="Subtopic (optional)">
          <select className={selectCls} style={selectStyle} value={value.subtopic || ""}
            onChange={(e) => onChange({ ...value, subtopic: e.target.value || null })}
            data-testid="ts-subtopic-select">
            <option value="">Any subtopic</option>
            {topic.subtopics.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      )}
    </>
  );
}

function CountPicker({ min, max, value, onChange }) {
  const opts = [];
  for (let i = min; i <= max; i += 1) opts.push(i);
  return (
    <div className="flex gap-2" data-testid="ts-count-picker">
      {opts.map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className="w-11 h-11 rounded-xl font-display font-bold transition-transform hover:-translate-y-0.5"
          style={{ background: value === n ? "var(--primary)" : "var(--paper)", color: value === n ? "white" : "var(--text)", border: `1.5px solid ${value === n ? "var(--primary)" : "var(--border)"}` }}
          data-testid={`ts-count-${n}`}>{n}</button>
      ))}
    </div>
  );
}

export default function PracticeConfig({ section, mode, modeName, passageTypes, cost, busy, error, onStart }) {
  const [sel, setSel] = useState({ category_code: null, topic_id: null, subtopic: null });
  const [count, setCount] = useState(mode === "mixed" ? 3 : 1);
  const [timer, setTimer] = useState(null);
  const [draft, setDraft] = useState(null);
  const [ptype, setPtype] = useState("");
  const [min, max] = mode === "mixed" ? [2, 4] : mode === "timed" ? [1, 4] : [1, 3];
  const n = Math.min(Math.max(count, min), max);
  const minutes = timer ?? n * 9;
  const canStart = mode !== "topic" || sel.topic_id;
  const total = useMemo(() => +(cost * n).toFixed(2), [cost, n]);

  const start = () => onStart({
    section_id: section.id, mode, passage_count: n,
    topic_id: mode === "topic" ? sel.topic_id : null,
    subtopic: mode === "topic" ? sel.subtopic : null,
    category_code: ["mixed", "experimental", "graph", "calculation", "timed"].includes(mode) ? sel.category_code : null,
    timer_minutes: mode === "timed" ? minutes : null,
    passage_type: ptype || null,
  });

  return (
    <div className="pm-card p-6" data-testid="ts-config-panel">
      <div className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--text-muted)" }}>{section.short} · setup</div>
      <h3 className="font-display text-xl font-bold mb-5" data-testid="ts-config-title">{modeName}</h3>
      {mode === "topic" ? (
        <TopicPicker section={section} value={sel} onChange={setSel} />
      ) : mode !== "weakness" && (
        <Field label="Limit to one category (optional)">
          <select className={selectCls} style={selectStyle} value={sel.category_code || ""}
            onChange={(e) => setSel({ ...sel, category_code: e.target.value || null })} data-testid="ts-filter-category-select">
            <option value="">Whole {section.short} section</option>
            {section.categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Passage type">
        <select className={selectCls} style={selectStyle} value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="ts-passage-type-select">
          <option value="">Any type (rotates A–F)</option>
          {(passageTypes || []).map((t) => <option key={t.id} value={t.id}>Type {t.id} · {t.name}</option>)}
        </select>
        {ptype && <span className="block text-xs mt-1.5" style={{ color: "var(--text-muted)" }} data-testid="ts-passage-type-desc">{passageTypes.find((t) => t.id === ptype)?.desc}</span>}
      </Field>
      <Field label="Passages (5–6 questions each)">
        <CountPicker min={min} max={max} value={n} onChange={setCount} />
      </Field>
      {mode === "timed" && (
        <Field label="Timer (minutes)">
          <div className="flex items-center gap-2 flex-wrap">
            {[n * 7, n * 9, n * 12].map((m) => (
              <button key={m} type="button" onClick={() => setTimer(m)} className="pm-badge"
                style={{ background: minutes === m ? "var(--primary)" : "var(--muted)", color: minutes === m ? "white" : "var(--text-secondary)" }}
                data-testid={`ts-timer-preset-${m}`}>{m} min</button>
            ))}
            <input type="number" min={5} max={180} value={draft ?? minutes} onChange={(e) => setDraft(e.target.value)}
              onBlur={() => { if (draft != null) setTimer(Math.max(5, Math.min(180, +draft || 5))); setDraft(null); }}
              className="w-20 rounded-xl px-2 py-1.5 text-sm" style={selectStyle} data-testid="ts-timer-input" />
          </div>
        </Field>
      )}
      <div className="flex items-center gap-2 text-sm mb-4" style={{ color: "var(--text-secondary)" }} data-testid="ts-cost-line">
        <Coins size={14} />
        {cost ? <>{cost} credit per passage you haven't done before · up to <b>{total} credits</b></> : <>Included in your plan · no credits used</>}
      </div>
      <button onClick={start} disabled={!canStart || busy} className="pm-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ts-start-btn">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Start practice
      </button>
      {error && (
        <div className="text-sm mt-3" style={{ color: "var(--weakness)" }} data-testid="ts-start-error">
          {error.message}
          {error.paywall && <> · <a href="/app/plans" className="underline" data-testid="ts-start-upgrade-link">Upgrade or buy credits</a></>}
        </div>
      )}
    </div>
  );
}
